import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { standardGetHttpOptions, standardHttpOptions } from '../models';
import { LoadingService } from './loading.service';
import { Observable } from 'rxjs';
import { throwError } from 'rxjs';
import { KigSettings, KIG_SETTINGS } from '../../../settings/kig-settings';
import { UserService } from '@services/user.service';
import { of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { ActionQueue as ActionQueue } from '@models/ActionQueue/ActionQueue.model';
import { ActionQueueLive } from '@models/ActionQueue/ActionQueueLive.model';


@Injectable()
export class ActionQueueService {
  public standardHttpOptionsWithUser: any;

  private apiUrl: string;

  // Cached variables
  public actionQueues: ActionQueue[] = [];

  
  constructor(private http: HttpClient, private loadingService: LoadingService, private userService: UserService, 
                @Inject(KIG_SETTINGS) private settings: KigSettings) {

    this.apiUrl = this.settings.apiUrl;
  }


 public clearCachedVariables() {
    this.actionQueues = [];
  }


  buildStandardHttpOptionsWithUser(username: string) {
    this.standardHttpOptionsWithUser = {
      withCredentials: true,
      headers: new HttpHeaders({
        ['Content-Type']: 'application/json',
        username
      })
    };
  }

 
  public getActionQueues(loadMessage: string, forceReload: boolean): Observable<any> {
    if (this.actionQueues.length > 0 && !forceReload) {
      return new Observable<ActionQueue[]>((obs) => {
        obs.next(this.actionQueues);
        obs.complete(); 
      });
    }

    if (loadMessage !== null && loadMessage !== '') {
      this.loadingService.setMessage(loadMessage);
    }
    return this.http.get(`${ this.apiUrl }/actionQueue`, standardGetHttpOptions).pipe(
      map((response: ActionQueue[]) => {
        this.actionQueues = response;
        return this.actionQueues;
      }),
      catchError(this.handleError),
      finalize(() => {
        if (loadMessage !== null && loadMessage !== '') {
          this.loadingService.clearMessage();
        }
      })
    );
  }


  public updateActionQueues(actionQueue: ActionQueue, loadMessage: string) {
    if (loadMessage != null && loadMessage !== '') {
      this.loadingService.setMessage(loadMessage);
    }

    return this.http.put(`${ this.apiUrl }/actionQueue`, JSON.stringify(actionQueue), standardHttpOptions).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => {
        if (loadMessage != null && loadMessage !== '') {
          this.loadingService.clearMessage();
        }
      })
    );
  }

  
  public createActionQueue(actionQueue: ActionQueue) {
    this.loadingService.setMessage('Creating action queue ...');
    return this.http.post(`${ this.apiUrl }/actionQueue/`, JSON.stringify(actionQueue), standardHttpOptions).pipe(
      map((response: Response) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public deleteActionQueue(actionQueue: ActionQueue) {
    this.loadingService.setMessage('Deleting action queue ...');
    return this.http.delete(`${ this.apiUrl }/actionQueue/${ actionQueue.id }`, standardHttpOptions).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }

  
  public setEventsToUseActionQueue(eventIds: number[], actionQueueId: number, loadMessage: string) {
    if (loadMessage != null && loadMessage !== '') {
      this.loadingService.setMessage(loadMessage);
    }
    return this.http.post(`${ this.apiUrl }/actionQueue/SetEventsToUseActionQueue/${ actionQueueId }/`, JSON.stringify(eventIds), standardHttpOptions).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => {
        if (loadMessage != null && loadMessage !== '') {
          this.loadingService.clearMessage();
        }
      })
    );
  }


  public cancelQueuedItem(actionQueueLiveId: number, loadMessage: string) {
    if (loadMessage != null && loadMessage !== '') {
      this.loadingService.setMessage(loadMessage);
    }
    return this.http.put(`${ this.apiUrl }/actionQueue/cancelQueuedItem/${ actionQueueLiveId }`, null, standardHttpOptions).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => {
        if (loadMessage != null && loadMessage !== '') {
          this.loadingService.clearMessage();
        }
      })
    );
  }


  public restoreCanceledQueuedItem(actionQueueLiveId: number, loadMessage: string) {
    if (loadMessage != null && loadMessage !== '') {
      this.loadingService.setMessage(loadMessage);
    }
    return this.http.put(`${ this.apiUrl }/actionQueue/restoreCanceledQueuedItem/${ actionQueueLiveId }`, null, standardHttpOptions).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => {
        if (loadMessage != null && loadMessage !== '') {
          this.loadingService.clearMessage();
        }
      })
    );
  }


  public executeQueueItem(actionQueueLiveId: number, loadMessage: string) {
    if (loadMessage != null && loadMessage !== '') {
      this.loadingService.setMessage(loadMessage);
    }
    return this.http.put(`${ this.apiUrl }/actionQueue/executeQueuedItem/${ actionQueueLiveId }`, null, standardHttpOptions).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => {
        if (loadMessage != null && loadMessage !== '') {
          this.loadingService.clearMessage();
        }
      })
    );
  }


  public updateQueuedItem(actionQueueLive: ActionQueueLive, loadMessage: string) {
    if (loadMessage != null && loadMessage !== '') {
      this.loadingService.setMessage(loadMessage);
    }

    return this.http.put(`${ this.apiUrl }/actionQueue/updateQueuedItem/${ actionQueueLive.id }`, JSON.stringify(actionQueueLive), standardHttpOptions).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => {
        if (loadMessage != null && loadMessage !== '') {
          this.loadingService.clearMessage();
        }
      })
    );
  }


  private handleError(error: any) {
    if (error.status === 404) {
      return of(null);
    }
    return throwError(() => error);
  }
}
